import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Icon } from '../../icons';
import { createAuthSociety } from '../../api/onboarding';
import { useAuth } from '../../context/AuthContext';

function AuthField({ label, type = "text", value, onChange, placeholder, note, icon, error }) {
  return (
    <div>
      {label && <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">{label}</label>}
      <div className="relative">
        {icon && (
          <div className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none flex ${error ? 'text-red-600 dark:text-red-400' : 'text-slate-400 dark:text-slate-500'}`}>
            <Icon name={icon} size={15} />
          </div>
        )}
        <input 
          type={type} 
          value={value} 
          onChange={onChange} 
          placeholder={placeholder}
          className={`w-full text-sm rounded-xl outline-none transition-all ${icon ? 'py-2.5 pr-10 pl-9' : 'py-2.5 px-3.5'} ${
            error 
              ? 'bg-red-50 dark:bg-red-900/10 border-2 border-red-500 text-red-900 dark:text-red-200 focus:ring-4 focus:ring-red-500/20' 
              : 'bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-4 focus:ring-primary/20 placeholder-slate-400 dark:placeholder-slate-500'
          }`}
        />
      </div>
      {error && <p className="text-[11px] text-red-600 dark:text-red-400 mt-1 font-medium">{error}</p>}
      {note && !error && <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">{note}</p>}
    </div>
  );
}

export default function CreateSocietyScreen({ onNav }) {
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [redirectUrl, setRedirectUrl] = useState("");

  const [society, setSociety] = useState({ name: "", type: "CHS", city: "", address: "", units: "", reg: "" });
  const [plan, setPlan] = useState("gold_plus");

  const clearErr = k => setFieldErrors(e => ({ ...e, [k]: "" }));
  const ss = k => e => {
    setSociety(s => ({ ...s, [k]: e.target.value }));
    clearErr(k === "name" ? "society_name" : k === "units" ? "total_units" : k === "reg" ? "registration_no" : k);
  };

  const plans = [
    { id: "free", maxUnits: 20, name: "Free", priceMonthly: "0", priceYearly: "0", per: "/ unit", units: "Manage your society at zero cost", features: ["Unit & Payment Management", "Basic Dashboard Overview", "Notice Board (5/month)", "Single Admin User"], color: "#64748b" },
    { id: "gold", maxUnits: 100, name: "Gold", priceMonthly: "10", priceYearly: "5", per: "/ unit", units: "Professional billing, vendors & reporting", features: ["Everything in Free", "Maintenance Billing & PDF Receipts", "Vendor & AMC Management", "Amenities & Task Management"], color: "#4f46e5" },
    { id: "gold_plus", maxUnits: 300, name: "Gold+", priceMonthly: "20", priceYearly: "10", per: "/ unit", units: "Gate security, compliance & operations", features: ["Everything in Gold", "Gate & Visitor Security", "Bank Account & Finance Management", "Emergency Alert System"], color: "#10b981", popular: true },
    { id: "gold_max", maxUnits: 999999, name: "GoldMax", priceMonthly: "30", priceYearly: "15", per: "/ unit", units: "Unlimited scale. Enterprise governance.", features: ["Everything in Gold+", "Bank Statement Import", "Election & Voting Module", "Multi-Channel Emergency Alerts"], color: "#7c3aed" },
  ];

  const validate1 = () => {
    const errs = {};
    if (!society.name) errs.society_name = "Society name is required";
    if (!society.city) errs.city = "City is required";
    if (!society.units) errs.total_units = "Total units is required";
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setError("Please fix the errors below.");
      return false;
    }
    return true;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const script = document.createElement('script');
      script.src = process.env.REACT_APP_RAZORPAY_CHECKOUT_URL || 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const next = async () => {
    setError("");
    setFieldErrors({});

    if (step === 1 && !validate1()) return;

    if (step === 1) {
      const units = parseInt(society.units || 0);
      if (units > plans.find(p => p.id === plan)?.maxUnits) {
        const bestPlan = plans.find(p => p.maxUnits >= units) || plans[plans.length - 1];
        setPlan(bestPlan.id);
      }
    }

    if (step === 2) {
      setLoading(true);
      try {
        const payload = {
          society_name: society.name,
          society_slug: society.name.toLowerCase().trim().replace(/ /g, '-').replace(/[^\w-]+/g, ''),
          society_type: society.type,
          city: society.city,
          address: society.address,
          total_units: parseInt(society.units),
          registration_no: society.reg,
          plan_slug: plan,
          billing_cycle: 'yearly'
        };
        const response = await createAuthSociety(payload);

        if (response.data.requires_payment) {
          const res = await loadRazorpayScript();
          if (!res) {
            setError('Razorpay SDK failed to load.');
            setLoading(false);
            return;
          }
          const options = {
            key: response.data.razorpay_key,
            subscription_id: response.data.razorpay_subscription_id,
            name: "The Clover App",
            description: "Society Subscription Plan",
            handler: function (rzpResponse) {
              const redirectPort = window.location.port ? `:${window.location.port}` : '';
              setRedirectUrl(`${window.location.protocol}//${payload.society_slug}.${window.location.hostname}${redirectPort}/login?registered=true`);
              setStep(3);
            },
            theme: { color: "#4f46e5" }
          };
          const rzp = new window.Razorpay(options);
          rzp.on('payment.failed', function () { setError('Payment failed.'); });
          rzp.open();
        } else {
          setRedirectUrl(response.data.redirect_url);
          setStep(3);
        }
      } catch (err) {
        if (err.errors) {
          setFieldErrors(err.errors);
          if (['society_name', 'society_slug', 'city', 'address', 'total_units', 'registration_no'].some(f => err.errors[f])) {
            setStep(1);
          }
          setError("Validation failed. Please check the fields.");
        } else {
          setError(err.message || "Unable to create society.");
        }
      } finally {
        setLoading(false);
      }
      return;
    }
    setStep(s => s + 1);
  };

  const prev = () => { setError(""); if (step > 1) setStep(s => s - 1); };

  const handleGoToLogin = async () => {
    if (refreshUser) {
      await refreshUser();
    }
    onNav('dashboard');
  };

  const STEP_LABELS = ["Society Info", "Choose Plan", "All Set!"];

  return (
    <div className="max-w-2xl mx-auto py-8">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-border p-6 md:p-8">
        
        {step !== 3 && (
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 10 }}>
              {STEP_LABELS.slice(0, 2).map((l, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", flex: i < 1 ? 1 : "none" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: step > i + 1 ? "#10b981" : step === i + 1 ? "linear-gradient(135deg,#4f46e5,#7c3aed)" : "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", transition: "all .3s" }}>
                      {step > i + 1 ? <Icon name="check" size={12} color="white" strokeWidth={2.5} /> : <span style={{ fontSize: 11, fontWeight: 800, color: step === i + 1 ? "#fff" : "#94a3b8" }}>{i + 1}</span>}
                    </div>
                    <span style={{ fontSize: 9, fontWeight: 600, color: step === i + 1 ? "#4f46e5" : step > i + 1 ? "#10b981" : "#94a3b8", textTransform: "uppercase" }}>{l}</span>
                  </div>
                  {i < 1 && <div style={{ flex: 1, height: 2, background: step > i + 1 ? "#10b981" : "#e2e8f0", margin: "0 6px", marginBottom: 18 }} />}
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-xl text-sm mb-6 flex items-center gap-2 border border-red-200">
            <Icon name="alert" size={14} /> {error}
          </div>
        )}

        {step === 1 && (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-ink">Register new society</h2>
              <p className="text-sm text-muted mt-1">Tell us about the society you want to create</p>
            </div>
            <div className="space-y-4">
              <AuthField label="Society Name *" icon="building" value={society.name} onChange={ss("name")} placeholder="e.g. Sunrise Heights CHS" error={fieldErrors.society_name || fieldErrors.society_slug} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Society Type *</label>
                  <select value={society.type} onChange={ss("type")} className="w-full px-3.5 py-2.5 border-2 border-slate-200 dark:border-slate-700 rounded-xl text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 outline-none focus:border-primary focus:ring-4 focus:ring-primary/20 transition-all cursor-pointer">
                    {["Apartment Complex", "Villa / Gated Community", "Township", "Commercial Complex"].map(t => <option key={t} className="bg-white dark:bg-slate-800">{t}</option>)}
                  </select>
                </div>
                <AuthField label="City *" icon="building" value={society.city} onChange={ss("city")} placeholder="Mumbai" error={fieldErrors.city} />
              </div>
              <AuthField label="Full Address" value={society.address} onChange={ss("address")} placeholder="Plot No., Area, City - PIN" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <AuthField label="Total Units *" type="number" value={society.units} onChange={ss("units")} placeholder="e.g. 120" error={fieldErrors.total_units} />
                <AuthField label="Registration No." value={society.reg} onChange={ss("reg")} placeholder="Optional" error={fieldErrors.registration_no} />
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-ink">Choose your plan</h2>
              <p className="text-sm text-muted mt-1">Select a plan for {society.name}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {plans.map(p => {
                const isDisabled = parseInt(society.units || 0) > p.maxUnits;
                return (
                  <div key={p.id} onClick={() => {
                    if (isDisabled) {
                      toast.error(`The ${p.name} plan supports up to ${p.maxUnits} units. You entered ${society.units} units. Go back to update it.`, { duration: 5000, position: 'top-center' });
                    } else {
                      setPlan(p.id);
                    }
                  }} className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${plan === p.id ? 'border-primary bg-primary/5 dark:bg-primary/10' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{color: p.color}}>{p.name}</div>
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="text-xl font-black text-slate-900 dark:text-white">₹{p.priceYearly}</span>
                      <span className="text-xs text-slate-500 dark:text-slate-400">/ yr</span>
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 pb-3 border-b border-slate-200 dark:border-slate-700 mb-3">{p.units}</div>
                    <div className="space-y-1.5">
                      {p.features.map((feat, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-xs text-slate-700 dark:text-slate-300">
                          <Icon name="check" size={11} color={p.color} className="mt-0.5 shrink-0" />
                          {feat}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {step === 3 && (
          <div className="text-center py-6">
            <div className="mx-auto w-20 h-20 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mb-4">
              <Icon name="check" size={32} />
            </div>
            <h2 className="text-2xl font-bold mb-2 text-slate-900 dark:text-white">You're all set!</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              <strong className="text-slate-900 dark:text-white">{society.name}</strong> has been registered. We are provisioning your database.
            </p>
            <div className="flex items-center gap-2 mb-6 p-3 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800/30 rounded-lg text-sm text-left">
              <Icon name="mail" size={16} className="shrink-0" />
              <span><strong>You will receive an email</strong> once your society is ready to use.</span>
            </div>
            <button onClick={handleGoToLogin} className="w-full py-3 px-4 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors">
              Go to Dashboard
            </button>
          </div>
        )}

        {step < 3 && (
          <div className="flex gap-3 mt-8">
            {step > 1 && (
              <button onClick={prev} disabled={loading} className="flex-1 py-3 px-4 border-2 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-xl font-semibold bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                Back
              </button>
            )}
            <button onClick={next} disabled={loading} className="flex-[2] py-3 px-4 bg-primary hover:bg-primary/90 transition-colors text-white rounded-xl font-semibold flex justify-center items-center gap-2">
              {loading ? "Setting up..." : step === 2 ? "Complete Registration" : "Continue"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
